// Login.jsx — Login, registration, phone auth, MFA challenge, MFA setup modal.
//
// FROZEN (mostly) — change branding text or add auth providers here.
// Exports: MfaSetupModal (named), Login (default)

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../constants';
import { Inp, Btn } from './ui';
import { auth } from '../api';
import DebugLogger from '../debugLogger';

// ── MfaSetupModal ─────────────────────────────────────────────────────────────
// Triggered from Settings panel when user enables 2FA.
export function MfaSetupModal({ onClose, onDone }) {
  const C = useTheme();
  const [step, setStep] = useState('loading'); // loading | scan | done
  const [qr, setQr]     = useState(null);
  const [code, setCode] = useState('');
  const [err, setErr]   = useState('');

  useEffect(() => {
    auth.mfaSetup().then(d => { setQr(d.qr); setStep('scan'); }).catch(() => setErr('Failed to generate MFA secret'));
  }, []);

  const verify = async () => {
    try { await auth.mfaVerify(code); setStep('done'); setTimeout(onDone, 1200); }
    catch (e) { setErr(e.message); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: '32px 36px', width: 380, textAlign: 'center' }}>
        <div style={{ fontSize: 28, marginBottom: 10 }}>🔐</div>
        <div style={{ fontWeight: 700, fontSize: 17, color: C.text, marginBottom: 6 }}>Enable Two-Factor Auth</div>
        {step === 'loading' && <div style={{ color: C.muted, fontSize: 14 }}>Generating secret...</div>}
        {step === 'scan' && <>
          <div style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>Scan this QR code with Google Authenticator or Authy</div>
          {qr && <img src={qr} alt="QR" style={{ width: 180, height: 180, borderRadius: 12, marginBottom: 16 }}/>}
          <Inp label="Enter 6-digit code to confirm" value={code} onChange={setCode} type="tel" placeholder="000000" onKeyDown={e => e.key === 'Enter' && verify()}/>
          {err && <div style={{ color: '#EF4444', fontSize: 12, marginBottom: 8 }}>{err}</div>}
          <Btn onClick={verify}>Verify &amp; Enable</Btn>
        </>}
        {step === 'done' && <div style={{ color: C.accent, fontSize: 15, fontWeight: 600, padding: '16px 0' }}>✓ MFA enabled!</div>}
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 13, marginTop: 8 }}>Cancel</button>
      </div>
    </div>
  );
}

// ── Login ─────────────────────────────────────────────────────────────────────
export default function Login({ onLogin }) {
  const C        = useTheme();
  const isTavern = C.name === 'Tavern';

  const [view, setView]       = useState('login');
  const [email, setEmail]     = useState('');
  const [pass, setPass]       = useState('');
  const [uname, setUname]     = useState('');
  const [phone, setPhone]     = useState('');
  const [mfaCode, setMfaCode] = useState(['', '', '', '', '', '']);
  const [mfaUid, setMfaUid]   = useState(null);
  const [err, setErr]         = useState('');
  const [loading, setLoading] = useState(false);
  const refs = useRef([]);

  const handle = async fn => { setErr(''); setLoading(true); try { await fn(); } catch (e) { setErr(e.message); } finally { setLoading(false); } };

  const saveToken = (token, user) => { localStorage.setItem('tavern_token', token); DebugLogger.setToken(token); onLogin(user); };

  const doLogin = () => handle(async () => {
    const d = await auth.login({ email, password: pass });
    if (d.mfa_required) { setMfaUid(d.user_id); setView('mfa'); return; }
    saveToken(d.token, d.user);
  });

  const doRegister = () => handle(async () => {
    const d = await auth.register({ username: uname, email, password: pass });
    saveToken(d.token, d.user);
  });

  const doPhoneSend   = () => handle(async () => { await auth.phoneSend(phone); setView('phone_verify'); });

  const doPhoneVerify = () => handle(async () => {
    const d = await auth.phoneVerify(phone, mfaCode.join(''));
    if (d.mfa_required) { setMfaUid(d.user_id); setView('mfa'); return; }
    saveToken(d.token, d.user);
  });

  const doMfaChallenge = () => handle(async () => {
    const d = await auth.mfaChallenge({ user_id: mfaUid, code: mfaCode.join('') });
    saveToken(d.token, d.user);
  });

  const handleMfaKey = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...mfaCode]; next[i] = val.slice(-1); setMfaCode(next);
    if (val && i < 5) refs.current[i + 1]?.focus();
  };

  const handleMfaKeyDown = (e, i) => {
    if (e.key === 'Enter' && !mfaCode.some(d => !d)) {
      view === 'mfa' ? doMfaChallenge() : doPhoneVerify();
    }
    if (e.key === 'Backspace' && !mfaCode[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const handleMfaPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    const next = ['', '', '', '', '', ''];
    text.split('').forEach((ch, i) => { next[i] = ch; });
    setMfaCode(next);
    refs.current[Math.min(text.length, 5)]?.focus();
  };

  const mfaInput = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 }} onPaste={handleMfaPaste}>
      {mfaCode.map((d, i) => (
        <input key={i} ref={el => refs.current[i] = el} value={d}
          onChange={e => handleMfaKey(i, e.target.value)}
          onKeyDown={e => handleMfaKeyDown(e, i)}
          onPaste={handleMfaPaste}
          inputMode="numeric" pattern="[0-9]*" autoComplete={i === 0 ? 'one-time-code' : 'off'}
          style={{ width: 46, height: 54, borderRadius: 10, textAlign: 'center', fontSize: 20, fontWeight: 700, fontFamily: 'monospace', background: '#0D0F14', border: `1px solid ${C.border}`, color: C.accent, outline: 'none' }}/>
      ))}
    </div>
  );

  const viewTitle = view === 'mfa'          ? 'Verify Identity'
    : view.startsWith('phone')              ? 'Phone Login'
    : view === 'register'                   ? (isTavern ? 'Join the Tavern' : 'Create Account')
    :                                         (isTavern ? 'Enter the Tavern' : 'Sign in to Tavern');

  return (
    <div style={{ minHeight: '100vh', background: isTavern ? 'radial-gradient(ellipse at 30% 60%, #2A1A06 0%, #1A1008 40%, #0E0803 100%)' : C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: C.font, position: 'relative' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono&family=Crimson+Pro:ital,wght@0,400;0,600;0,700;1,400&family=Cinzel:wght@700;900&display=swap'); input::placeholder{color:${C.muted}66;} input,textarea{font-family:${C.font};} @keyframes candleFlicker{0%,100%{text-shadow:0 0 8px #C8922A88;}50%{text-shadow:0 0 20px #C8922Acc;}}`}</style>

      {/* Background */}
      {isTavern ? (<>
        <div style={{ position: 'fixed', inset: 0, background: 'radial-gradient(ellipse at 50% 100%, #C8922A0A, transparent 70%)', pointerEvents: 'none' }}/>
        <div style={{ position: 'fixed', inset: 0, backgroundImage: 'repeating-linear-gradient(170deg, transparent, transparent 40px, rgba(200,146,42,0.015) 40px, rgba(200,146,42,0.015) 41px)', pointerEvents: 'none' }}/>
      </>) : (<>
        <div style={{ position: 'fixed', inset: 0, backgroundImage: `linear-gradient(${C.border}44 1px,transparent 1px),linear-gradient(90deg,${C.border}44 1px,transparent 1px)`, backgroundSize: '40px 40px', pointerEvents: 'none' }}/>
        <div style={{ position: 'fixed', inset: 0, background: `radial-gradient(ellipse at 50% 0%, ${C.accent}0D, transparent 60%)`, pointerEvents: 'none' }}/>
      </>)}

      <div style={{ background: isTavern ? 'linear-gradient(160deg, #2A1A0A 0%, #1E1208 100%)' : C.card, border: isTavern ? '1px solid #4A2E10' : '1px solid #1E2128', borderRadius: isTavern ? 8 : 20, padding: '36px 40px', width: 400, position: 'relative', boxShadow: isTavern ? '0 24px 80px rgba(0,0,0,0.8), inset 0 1px 0 rgba(200,146,42,0.1)' : '0 24px 80px rgba(0,0,0,0.6)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/logo192.png" alt="Tavern" style={{ width: 64, height: 64, borderRadius: isTavern ? 12 : 18, margin: '0 auto 14px', display: 'block', boxShadow: `0 8px 24px ${C.accent}44` }}/>
          <div className={isTavern ? 'tv-heading' : ''} style={{ fontSize: 22, fontWeight: 700, color: C.text, fontFamily: C.font }}>{viewTitle}</div>
          {isTavern && view === 'login' && <div style={{ fontSize: 12, color: C.muted, marginTop: 4, fontStyle: 'italic' }}>Pull up a chair, adventurer</div>}
        </div>

        {err && <div style={{ background: '#EF444411', border: '1px solid #EF444433', color: '#EF4444', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 14 }}>{err}</div>}

        {view === 'mfa' && <>
          <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', marginBottom: 16 }}>Enter your 6-digit authenticator code</div>
          {mfaInput}
          <Btn onClick={doMfaChallenge} disabled={loading || mfaCode.some(d => !d)}>Verify</Btn>
          <div style={{ textAlign: 'center' }}><span onClick={() => setView('login')} style={{ color: C.accent, cursor: 'pointer', fontSize: 13 }}>← Back</span></div>
        </>}

        {view === 'phone' && <>
          <Inp label="Phone Number" value={phone} onChange={setPhone} type="tel" placeholder="+1 555 000 0000" onKeyDown={e => e.key === 'Enter' && doPhoneSend()}/>
          <Btn onClick={doPhoneSend} disabled={loading}>Send Code</Btn>
          <div style={{ textAlign: 'center' }}><span onClick={() => setView('login')} style={{ color: C.accent, cursor: 'pointer', fontSize: 13 }}>← Back</span></div>
        </>}

        {view === 'phone_verify' && <>
          <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', marginBottom: 16 }}>Enter the 6-digit code sent to {phone}</div>
          {mfaInput}
          <Btn onClick={doPhoneVerify} disabled={loading || mfaCode.some(d => !d)}>Verify</Btn>
        </>}

        {(view === 'login' || view === 'register') && <>
          {view === 'register' && <Inp label="Username" value={uname} onChange={setUname} placeholder="your_handle" onKeyDown={e => e.key === 'Enter' && (view === 'register' ? doRegister() : doLogin())}/>}
          <Inp label="Email" value={email} onChange={setEmail} type="email" placeholder="you@example.com" onKeyDown={e => e.key === 'Enter' && (view === 'register' ? doRegister() : doLogin())}/>
          <Inp label="Password" value={pass} onChange={setPass} type="password" placeholder="••••••••" onKeyDown={e => e.key === 'Enter' && (view === 'register' ? doRegister() : doLogin())}/>

          {view === 'register' && (
            <div style={{ background: '#0D0F14', border: '1px solid #1A1D24', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#4B5563', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>⚖️ Age Verification</div>
              <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6 }}>
                By creating an account you confirm that you are{' '}
                <span style={{ color: '#CBD5E1', fontWeight: 700 }}>at least 18 years old</span>, or that you are{' '}
                <span style={{ color: '#CBD5E1', fontWeight: 700 }}>lying about being 18</span>, which frankly is a skill we respect.
              </div>
              <div style={{ fontSize: 11, color: '#374151', marginTop: 8, fontStyle: 'italic' }}>
                Unlike Discord, we won't ask for your ID, your blood type, or a notarized letter from your parents. We simply trust that you're a functioning adult making responsible decisions — a bar so low it's basically underground, yet here we are.
              </div>
            </div>
          )}

          <Btn onClick={view === 'login' ? doLogin : doRegister} disabled={loading}>
            {loading ? 'Please wait...' : view === 'login' ? 'Sign In' : 'Create Account'}
          </Btn>
          <button onClick={() => setView('phone')} style={{ width: '100%', padding: '10px 0', borderRadius: 10, border: `1px solid ${C.border}`, background: '#0D0F14', color: C.text, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 14 }}>
            📱 Phone Number
          </button>
          <div style={{ textAlign: 'center', fontSize: 13, color: C.muted }}>
            {view === 'login' ? 'New here? ' : 'Have an account? '}
            <span onClick={() => setView(view === 'login' ? 'register' : 'login')} style={{ color: C.accent, cursor: 'pointer', fontWeight: 600 }}>
              {view === 'login' ? 'Sign up' : 'Sign in'}
            </span>
          </div>
        </>}
      </div>
    </div>
  );
}
